import * as React from 'react'

import { inject, observer } from 'mobx-react'

import MapBoxViewerStore from '~/client/src/shared/components/MapBoxViewer/MapBoxViewer.store'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import { KeyboardClaim } from '~/client/src/shared/utils/keyboardClaim'

import DesktopMarkupEditor from './DesktopMarkupEditor'
import MarkupEditorStore, {
  IMarkupEditorDeps,
  IMarkupEditorHandles,
  MarkupLocationSnapshot,
} from './MarkupEditorStore'
import { MarkupEditorTextChannel } from './markupEditorTextChannel'
import { IShortcutGroup } from './MarkupShortcutsLegend'
import MobileMarkupEditor from './MobileMarkupEditor'

import './MarkupEditor.css'

interface IProps {
  mapBoxViewerStore: MapBoxViewerStore
  sitePermitId: string
  projectId: string
  onClose: () => void
  /** Hands the parent the editor's imperative surface once ready. */
  onReady?: (handles: IMarkupEditorHandles | null) => void
  /** Reports whether there are uncommitted markup edits (for a close guard). */
  onDirtyChange?: (dirty: boolean) => void
  // Folds the committed pin location into each undo/redo snapshot and re-applies
  // it on undo/redo (unified markup + location timeline).
  getLocation?: () => MarkupLocationSnapshot
  onRestoreLocation?: (location: MarkupLocationSnapshot) => void
  markupsStore?: MarkupsStore
  // Touch/mobile build: drops keyboard-shortcut UI (legend, Kbd keycaps, the `I`
  // toggle) and pins the context toolbar above the markup instead of letting it float.
  isMobileEnv?: boolean
  // Creation flow: buffer markups locally (no SaveMarkup) under the temp
  // sitePermitId; the creation store flushes them to the real id post-create.
  deferred?: boolean
  // Copy-from-other-workflow: the reference markup (other workflow, same map)
  // currently picked as a copy source, and a setter to clear it when an editable
  // markup is selected instead. Cmd/Ctrl+C copies it into THIS workflow.
  copySelectedId?: string | null
  onCopySelect?: (id: string | null) => void
  // Precise-location edit, surfaced as a pin tool in the markup toolbar; the
  // behavior lives in the parent. showLocationTool renders the tool;
  // isLocationEditActive highlights it + makes the markup editor inert.
  showLocationTool?: boolean
  isLocationEditActive?: boolean
  onEditPreciseLocation?: () => void
  // Host-specific shortcut groups prepended to the legend.
  legendExtraGroups?: IShortcutGroup[]
  // Defer terra-draw (and its GL layers) until a tool pick / paste / drawingEngaged
  // — keeps a browse-session map free of the editor's per-frame layer cost.
  deferDrawing?: boolean
  // Host signal that drawing is (about to be) needed — e.g. a workflow creation
  // started — so the deferred controller boots even before any tool pick.
  drawingEngaged?: boolean
  onMarkupActivity?: () => void
  onIdleSelectChange?: (isIdle: boolean) => void
  onTextSubjectChange?: (isText: boolean) => void
  portalContainer?: HTMLElement | null
  // Set by a host that stacks over another editor, so its keys reach this one
  // instead of the map's underneath.
  keyboardClaim?: KeyboardClaim | null
  yieldToLayerIds?: string[]
  // The same for text markup layers, which queryRenderedFeatures cannot see.
  yieldToTextLayerIds?: string[]
  // The host's session text channel: while mounted, this editor feeds its session mount.
  editorText?: MarkupEditorTextChannel
  // Desktop only: while the form is minimized the toolbar becomes a common-wrapper cluster
  // — dockedTrailing is the composer's "Open workflow" action; onDockedGripDown drags the
  // whole cluster from its leading grip. Mobile uses its own peek flow, so both are ignored.
  dockedTrailing?: React.ReactNode
  onDockedGripDown?: (e: React.PointerEvent) => void
}

// Thin facade: builds the headless store from props + the injected markupsStore,
// drives its lifecycle, and picks the platform shell (the injected isMobileEnv).
// Counted, not a boolean: the flag lives on the root, so one editor unmounting while another is
// still open would otherwise clear it out from under the live session.
let openSessions = 0

@inject('markupsStore', 'isMobileEnv')
@observer
export default class MarkupEditor extends React.Component<IProps> {
  private readonly store = new MarkupEditorStore(this.toDeps())

  public componentDidMount(): void {
    // On the root, not a wrapper: the overlay sits above the map on some surfaces and beside it on
    // others, and the map attribution has to bound itself while the toolbar owns the bottom centre.
    openSessions += 1
    document.documentElement.dataset.markupSession = ''
    this.store.setMobileEnv(!!this.props.isMobileEnv)
    this.store.mount()
    if (this.props.drawingEngaged) this.store.ensureDrawing()
  }

  public componentDidUpdate(): void {
    this.store.syncDeps(this.toDeps())
    if (this.props.drawingEngaged) this.store.ensureDrawing()
  }

  public componentWillUnmount(): void {
    openSessions = Math.max(0, openSessions - 1)
    if (!openSessions) {
      delete document.documentElement.dataset.markupSession
    }
    // Drop the wired handles so none stale-fire on this now-dead editor.
    this.props.onReady?.(null)
    this.store.unmount()
  }

  private toDeps(): IMarkupEditorDeps {
    return {
      mapBoxViewerStore: this.props.mapBoxViewerStore,
      markupsStore: this.props.markupsStore,
      sitePermitId: this.props.sitePermitId,
      projectId: this.props.projectId,
      deferred: this.props.deferred,
      onClose: this.props.onClose,
      onReady: this.props.onReady,
      onDirtyChange: this.props.onDirtyChange,
      getLocation: this.props.getLocation,
      onRestoreLocation: this.props.onRestoreLocation,
      copySelectedId: this.props.copySelectedId,
      onCopySelect: this.props.onCopySelect,
      showLocationTool: this.props.showLocationTool,
      isLocationEditActive: this.props.isLocationEditActive,
      onEditPreciseLocation: this.props.onEditPreciseLocation,
      legendExtraGroups: this.props.legendExtraGroups,
      deferDrawingUntilEngaged: this.props.deferDrawing,
      onMarkupActivity: this.props.onMarkupActivity,
      onIdleSelectChange: this.props.onIdleSelectChange,
      onTextSubjectChange: this.props.onTextSubjectChange,
      keyboardClaim: this.props.keyboardClaim,
      yieldToLayerIds: this.props.yieldToLayerIds,
      yieldToTextLayerIds: this.props.yieldToTextLayerIds,
      editorText: this.props.editorText,
    }
  }

  public render() {
    const { portalContainer, dockedTrailing, onDockedGripDown } = this.props
    if (this.props.isMobileEnv) {
      return (
        <MobileMarkupEditor
          store={this.store}
          portalContainer={portalContainer}
        />
      )
    }
    return (
      <DesktopMarkupEditor
        store={this.store}
        portalContainer={portalContainer}
        dockedTrailing={dockedTrailing}
        onDockedGripDown={onDockedGripDown}
      />
    )
  }
}
